import React, { useRef, useState } from 'react';
import { ArrowRight, Check, ShieldCheck, Sparkles } from 'lucide-react';
import { Section, SectionHeading } from '../ui/Section';
import { SERVICES } from '../../lib/constants';
const serviceLinks: Record<string, string> = {
  'Managed Services': 'https://ctgroup.com.au/our-services/solutions/managed-services/',
  Security: 'https://ctgroup.com.au/our-services/security/managed-security-services/',
  Hosting: 'https://ctgroup.com.au/our-services/cloud-2/private-cloud-hosting/',
  Voice: 'https://ctgroup.com.au/our-services/voice/'
};
const serviceDetails: Record<string, {
  outcome: string;
  evidence: string[];
  reassurance: string;
}> = {
  'Managed Services': {
    outcome: 'Keep people productive and make escalation clear.',
    evidence: ['Dedicated engineering team, team leader and customer service manager', 'National helpdesk across Australian business hours', 'Central coordination of third-party technology vendors'],
    reassurance: 'Your team gets one place to ask for help and one accountable owner for escalation.'
  },
  Security: {
    outcome: 'Protect client trust, identity and business continuity.',
    evidence: ['Identity, endpoint, email and collaboration protection', 'Essential Eight guidance and vulnerability management', 'Managed detection, response and security reporting options'],
    reassurance: 'Controls are selected around the firm, its risk profile and its compliance requirements.'
  },
  Hosting: {
    outcome: 'Run critical applications on Australian private infrastructure.',
    evidence: ['Australian data residency across private data-centre locations', 'Top-tier physical security and multiple levels of redundancy', 'Application hosting, backups and recovery managed together'],
    reassurance: 'Private hosting is designed around control, remote access and business continuity.'
  },
  Voice: {
    outcome: 'Keep clients and teams connected wherever work happens.',
    evidence: ['Hosted VoIP and Microsoft Teams calling', 'Office, mobile and remote-work integration', 'Call-flow design and ongoing management'],
    reassurance: 'Telephony is treated as part of the technology environment, not another isolated supplier.'
  }
};
export function Services() {
  const [activeIndex, setActiveIndex] = useState(0);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const active = SERVICES[activeIndex];
  const detail = serviceDetails[active.name];
  const moveFocus = (index: number) => {
    const next = (index + SERVICES.length) % SERVICES.length;
    setActiveIndex(next);
    buttons.current[next]?.focus();
  };
  return <Section id="services" className="bg-white" ariaLabel="Services" data-id="element-359" data-mp="3o7czual3qis7">
      <div className="grid gap-8 lg:grid-cols-2 lg:items-end" data-id="element-360" data-mp="3s7ezke7a124u">
        <SectionHeading eyebrow="Explore the service model" title="Four capabilities. One accountable environment." body="Select a capability to see what CT Group coordinates and why it matters to a professional firm." data-id="element-361" data-mp="3s5u7cv8fpva5" />
        <div className="grid gap-4 border-l border-border pl-5 sm:grid-cols-2" data-id="element-362" data-mp="2zlgj3agzvqtf">
          <p className="flex gap-3 text-[14px] font-semibold leading-relaxed text-ink" data-id="element-363" data-mp="2zn9ut46gt18a"><ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-security" aria-hidden data-id="element-364" data-mp="2unwxtqlvqdrz" />Security is considered through every service.</p>
          <p className="flex gap-3 text-[14px] font-semibold leading-relaxed text-ink" data-id="element-365" data-mp="34kjjuipxzavl"><Sparkles className="mt-0.5 h-5 w-5 flex-shrink-0 text-ct-cyan" aria-hidden data-id="element-366" data-mp="34mmh9ntfc6ac" />AI adoption is grounded in governance and risk.</p>
        </div>
      </div>

      <div className="mt-10 border-y border-border" data-id="element-367" data-mp="3dkgagpvw6jta">
        <div role="tablist" aria-label="CT Group services" className="grid md:grid-cols-2 xl:grid-cols-4" data-id="element-368" data-mp="3sa4alnsc7cff">
          {SERVICES.map((service, index) => {
          const selected = index === activeIndex;
          return <button key={service.id} ref={element => {
            buttons.current[index] = element;
          }} type="button" role="tab" id={`service-tab-${service.id}`} aria-selected={selected} aria-controls="service-panel" tabIndex={selected ? 0 : -1} onClick={() => setActiveIndex(index)} onKeyDown={event => {
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              event.preventDefault();
              moveFocus(index + 1);
            }
            if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              event.preventDefault();
              moveFocus(index - 1);
            }
            if (event.key === 'Home') {
              event.preventDefault();
              moveFocus(0);
            }
            if (event.key === 'End') {
              event.preventDefault();
              moveFocus(SERVICES.length - 1);
            }
          }} className={`relative flex min-h-[190px] flex-col items-start border-b border-border p-6 text-left transition-[background-color,color] duration-200 ease-out md:border-r xl:border-b-0 ${selected ? 'bg-navy text-white' : 'bg-white text-navy hover:bg-surface-2'}`} data-id="element-369" data-mp="54mwoisy3mfx" data-mpi={index}>
                <span className="absolute inset-x-0 top-0 h-1" style={{
              background: service.accent
            }} aria-hidden data-id="element-370" data-mp="pdyzzpaggljk" />
                <span className={`text-[11px] font-bold uppercase tracking-[0.14em] ${selected ? 'text-white/70' : 'text-slate'}`} data-id="element-371" data-mp="le7000z4nnpt">0{index + 1}</span>
                <img src={service.logo} alt="" className={`mt-6 h-10 w-auto max-w-full object-contain ${selected ? 'brightness-0 invert' : ''}`} data-id="element-372" data-mp="bo40rj1exdj1" />
                <span className="sr-only" data-id="element-373" data-mp="6qc64h9dvdb5">{service.name}</span>
                <span className={`mt-auto flex items-center gap-2 pt-5 text-[12px] font-bold ${selected ? 'text-ct-light' : 'text-ct-deep'}`} data-id="element-374" data-mp="1peqabbuivct">{selected ? 'Currently selected' : 'View capability'} <ArrowRight className={`h-4 w-4 transition-transform duration-150 ease-out ${selected ? 'translate-x-1' : ''}`} aria-hidden data-id="element-375" data-mp="3ounj514yo9q1" /></span>
              </button>;
        })}
        </div>

        <div id="service-panel" role="tabpanel" aria-labelledby={`service-tab-${active.id}`} className="grid bg-surface-2 lg:grid-cols-2" data-id="element-376" data-mp="1n2hhm3xr0spk">
          <div className="flex flex-col justify-center border-b border-border p-7 lg:border-b-0 lg:border-r md:p-9" data-id="element-377" data-mp="2662ufz755v3f">
            <p className="text-[11px] font-bold uppercase tracking-[0.15em]" style={{
            color: active.accent
          }} data-id="element-378" data-mp="2b0gtqcoxcmdd">{active.name}</p>
            <h3 className="mt-4 max-w-lg font-display text-[30px] font-black leading-tight text-navy md:text-[38px]" data-id="element-379" data-mp="22627fq5z8bkr">{detail.outcome}</h3>
            <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-slate" data-id="element-380" data-mp="2pwodxd0c1nsn">{detail.reassurance}</p>
            <a href={serviceLinks[active.name]} className="mt-7 inline-flex items-center gap-2 self-start text-[14px] font-bold" style={{
            color: active.accent
          }} data-id="element-381" data-mp="2tvx6iejmzfmm">Explore {active.name} <ArrowRight className="h-4 w-4" aria-hidden data-id="element-382" data-mp="2kw0m4p4a52xd" /></a>
          </div>
          <ul className="grid sm:grid-cols-3 lg:grid-cols-1" data-id="element-383" data-mp="14ia4sgo5gi5s">
            {detail.evidence.map(item => <li key={item} className="flex min-h-[96px] items-center gap-3 border-b border-border p-5 last:border-b-0 sm:border-b-0 sm:border-r lg:border-b lg:border-r-0" data-id="element-384" data-mp="14kmw3kfss4vi"><Check className="h-5 w-5 flex-shrink-0" style={{
              color: active.accent
            }} aria-hidden data-id="element-385" data-mp="11wj54g1hj2ps" /><span className="text-[14px] font-semibold leading-relaxed text-ink" data-id="element-386" data-mp="wxptyu9o7nss">{item}</span></li>)}
          </ul>
        </div>
      </div>
    </Section>;
}